import { useEffect, useState } from 'react'
import { Phone, Mail, MapPin, Instagram, MessageCircle, Star, Tent, Mountain, Moon, Trees, ChevronDown, Menu, X } from 'lucide-react'

const LOGO = '/images/تل القمر 1.png'

const navItems = [
  { label: 'من نحن', id: 'about' },
  { label: 'النجوم', id: 'stars' },
  { label: 'المسارات', id: 'trails' },
  { label: 'التخييم', id: 'camping' },
  { label: 'الأكل', id: 'food' },
  { label: 'الورشات', id: 'workshops' },
  { label: 'الدفع الرباعي', id: 'offroad' },
  { label: 'تواصل', id: 'contact' },
]

function scrollToSection(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
}

export default function Home() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <div dir="rtl" className="font-sans bg-[#0d0a06] text-[#e8d9bc] min-h-screen">

      {/* ======= NAVBAR ======= */}
      <nav className={`fixed top-0 right-0 left-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0d0a06]/95 backdrop-blur-md shadow-lg shadow-black/40' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <img src={LOGO} alt="تل القمر" className="h-12 w-auto drop-shadow-lg" />
          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-6">
            {navItems.map(item => (
              <button key={item.id} onClick={() => scrollToSection(item.id)}
                className="text-[#c9a96e] hover:text-[#e8c97a] text-sm font-medium transition-colors duration-200 hover:drop-shadow-[0_0_6px_rgba(200,169,110,0.7)]">
                {item.label}
              </button>
            ))}
            <button onClick={() => scrollToSection('contact')}
              className="bg-[#b87333] hover:bg-[#d4904a] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-all duration-200 shadow-md">
              احجز الآن
            </button>
          </div>
          {/* Mobile Menu */}
          <button className="lg:hidden text-[#c9a96e]" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
        {menuOpen && (
          <div className="lg:hidden bg-[#0d0a06]/97 backdrop-blur-md border-t border-[#3a2810] px-4 py-4 flex flex-col gap-3">
            {navItems.map(item => (
              <button key={item.id} onClick={() => { scrollToSection(item.id); setMenuOpen(false) }}
                className="text-right text-[#c9a96e] hover:text-[#e8c97a] text-base py-1 transition-colors">
                {item.label}
              </button>
            ))}
            <button onClick={() => { scrollToSection('contact'); setMenuOpen(false) }}
              className="bg-[#b87333] text-white font-semibold py-2 px-4 rounded-lg mt-2">
              احجز الآن
            </button>
          </div>
        )}
      </nav>

      {/* ======= HERO ======= */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <img src="/images/IMG_7742.jpg" alt="تل القمر" className="absolute inset-0 w-full h-full object-cover object-center" style={{ filter: 'sepia(0.18) saturate(0.85) brightness(0.55) contrast(1.08)' }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/20 to-[#0d0a06]" />
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <img src={LOGO} alt="تل القمر" className="h-24 md:h-32 w-auto mx-auto mb-6 drop-shadow-[0_4px_20px_rgba(0,0,0,0.9)]" />
          <h1 className="text-5xl md:text-7xl font-bold text-[#e8d9bc] mb-4 leading-tight drop-shadow-[0_2px_12px_rgba(0,0,0,0.8)]" style={{ fontFamily: 'Cairo, sans-serif' }}>
            تَلُّ الْقَمَرِ
          </h1>
          <p className="text-xl md:text-2xl text-[#c9a96e] mb-4 font-medium italic">
            حَيْثُ تَلْتَقِي الْبَرِّيَّةُ بِالرُّوحِ
          </p>
          <p className="text-base md:text-lg text-[#a89070] mb-10">
            بَرِّيَّةُ الْقُدْسِ · بَيْتُ لَحْمٍ · فِلَسْطِينُ
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button onClick={() => scrollToSection('contact')}
              className="bg-[#b87333] hover:bg-[#d4904a] text-white font-bold text-lg px-8 py-4 rounded-xl shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(184,115,51,0.4)]">
              احجز تجربتك
            </button>
            <a href="https://wa.me/970598903500" target="_blank" rel="noopener noreferrer"
              className="border border-[#b87333] text-[#c9a96e] hover:bg-[#b87333]/10 font-semibold text-lg px-8 py-4 rounded-xl transition-all duration-300 flex items-center gap-2">
              <MessageCircle size={20} /> واتساب
            </a>
          </div>
        </div>
        <button onClick={() => scrollToSection('about')} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#c9a96e] animate-bounce z-10">
          <ChevronDown size={32} />
        </button>
      </section>

      {/* ======= FROM US ======= */}
      <section id="about" className="py-24 px-4">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="absolute -inset-2 rounded-2xl bg-gradient-to-br from-[#b87333]/20 to-transparent blur-xl" />
            <img src="/images/IMG_8003.jpg" alt="تأمل في البرية" className="relative rounded-2xl w-full h-[420px] object-cover object-center shadow-2xl" style={{ filter: 'sepia(0.22) saturate(0.88) brightness(0.92) contrast(1.06)' }} />
          </div>
          <div>
            <span className="text-[#b87333] text-sm font-semibold uppercase tracking-widest mb-3 block">من نحن</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#e8d9bc] mb-6 leading-snug" style={{ fontFamily: 'Cairo, sans-serif' }}>
              تَلُّ الْقَمَرِ — اسْمٌ لَمْ يُخْتَرْ
            </h2>
            <p className="text-[#a89070] text-lg leading-relaxed mb-5">
              تَلُّ الْقَمَرِ اسْمٌ لَمْ يُخْتَرْ؛ بَلِ اخْتَارَهُ الْمَكَانُ بِنَفْسِهِ. عِنْدَ اكْتِمَالِ الْقَمَرِ فَوْقَ هَذَا التَّلِّ — فِي سَمَاءٍ خَالِيَةٍ مِنَ التَّلَوُّثِ الضَّوْئِيِّ — يَبْدُو الْقَمَرُ وَكَأَنَّهُ يُلَامِسُ قِمَّةَ التَّلِّ، لَا يَعْلُو عَنْهَا أَكْثَرَ مِنْ قَامَةِ إِنْسَانٍ.
            </p>
            <p className="text-[#a89070] text-base leading-relaxed mb-8">
              نُؤْمِنُ أَنَّ التَّجْرِبَةَ الْحَقِيقِيَّةَ تُغَيِّرُ النَّاسَ. فِي تَلِّ الْقَمَرِ نَصْنَعُ فَضَاءَاتٍ تَجْعَلُ الْإِنْسَانَ يَتَعَلَّمُ وَيَتَأَمَّلُ وَيَنْفَتِحُ — مِنْ خِلَالِ الطَّبِيعَةِ وَالْأَرْضِ وَالسَّمَاءِ.
            </p>
            <div className="grid grid-cols-3 gap-4">
              {[{ icon: <Mountain size={22} />, label: 'برية القدس', sub: '500 كم²' },
                { icon: <Moon size={22} />, label: 'سماء نقية', sub: 'لا تلوث ضوئي' },
                { icon: <Trees size={22} />, label: 'طبيعة خلابة', sub: 'نباتات وحيوانات' }].map((item, i) => (
                <div key={i} className="bg-[#1a1108] border border-[#3a2810] rounded-xl p-4 text-center">
                  <div className="text-[#b87333] flex justify-center mb-2">{item.icon}</div>
                  <p className="text-[#e8d9bc] text-sm font-semibold">{item.label}</p>
                  <p className="text-[#7a6050] text-xs mt-1">{item.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ======= DESERT DIVIDER ======= */}
      <div className="relative h-64 overflow-hidden">
        <img src="/images/IMG_7767.jpg" alt="برية القدس" className="absolute inset-0 w-full h-full object-cover" style={{ filter: 'sepia(0.25) saturate(0.75) brightness(0.45) contrast(1.1)' }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d0a06] via-transparent to-[#0d0a06]" />
        <div className="absolute inset-0 flex items-center justify-center text-center px-4">
          <div>
            <p className="text-[#c9a96e] text-2xl md:text-3xl italic font-medium drop-shadow-lg">
              «تَمْشِي فِي بَرِّيَّةٍ شَهِدَتِ الْأَنْبِيَاءَ وَالرُّهْبَانَ»
            </p>
            <p className="text-[#7a6050] text-sm mt-2">بَرِّيَّةُ الْقُدْسِ — دار صلاح، شرق بيت لحم</p>
          </div>
        </div>
      </div>

      {/* ======= STARS ======= */}
      <section id="stars" className="py-24 px-4 bg-[#080604]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-[#b87333] text-sm font-semibold uppercase tracking-widest mb-3 block">رصد النجوم</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#e8d9bc] mb-4" style={{ fontFamily: 'Cairo, sans-serif' }}>
              السَّمَاءُ هُنَا مُخْتَلِفَةٌ
            </h2>
            <p className="text-[#a89070] text-lg max-w-2xl mx-auto">
              حِينَ تَرَى الْمَجَرَّةَ بِعَيْنِكَ لِأَوَّلِ مَرَّةٍ — شَيْءٌ فِيكَ يَتَغَيَّرُ
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="grid grid-cols-2 gap-4">
              <img src="/images/IMG_7778.jpg" alt="خسوف القمر" className="rounded-2xl w-full h-52 object-cover shadow-xl" style={{ filter: 'sepia(0.15) saturate(0.9) brightness(0.85) contrast(1.1)' }} />
              <img src="/images/IMG_8022.jpg" alt="مسارات النجوم" className="rounded-2xl w-full h-52 object-cover shadow-xl" style={{ filter: 'sepia(0.1) saturate(0.9) brightness(0.8) contrast(1.1)' }} />
              <img src="/images/IMG_7762.jpg" alt="تلسكوب" className="rounded-2xl w-full h-52 object-cover shadow-xl col-span-1" style={{ filter: 'sepia(0.2) saturate(0.85) brightness(0.88) contrast(1.06)' }} />
              <img src="/images/IMG_7759.jpg" alt="نار وتلسكوبات" className="rounded-2xl w-full h-52 object-cover shadow-xl" style={{ filter: 'sepia(0.2) saturate(0.85) brightness(0.88) contrast(1.06)' }} />
            </div>
            <div>
              <p className="text-[#a89070] text-base leading-relaxed mb-6">
                تَجْرِبَةٌ لَنْ تَجِدَهَا فِي أَيِّ مَكَانٍ آخَرَ فِي فِلَسْطِينَ. سَمَاءٌ نَقِيَّةٌ خَالِيَةٌ مِنَ التَّلَوُّثِ الضَّوْئِيِّ، مَعَ مُرْشِدٍ فَلَكِيٍّ مُتَخَصِّصٍ وَإِطْلَالَةٍ 360 دَرَجَةً.
              </p>
              <div className="space-y-4">
                {[
                  { emoji: '☕', title: 'قهوة عربية عند الغروب', desc: 'بداية الليلة بأصالة' },
                  { emoji: '🔭', title: 'مرشد فلكي متخصص', desc: 'علم وتأمل في ليلة واحدة' },
                  { emoji: '🌌', title: 'رؤية المجرة عارية', desc: 'حلقات زحل وأقمار المشتري' },
                  { emoji: '🔥', title: 'حوار حول النار', desc: 'أمسية لا تُنسى تحت النجوم' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 bg-[#13100a] border border-[#2a1f12] rounded-xl p-4">
                    <span className="text-2xl flex-shrink-0">{item.emoji}</span>
                    <div>
                      <p className="text-[#e8d9bc] font-semibold text-sm">{item.title}</p>
                      <p className="text-[#7a6050] text-xs mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======= TRAILS ======= */}
      <section id="trails" className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-[#b87333] text-sm font-semibold uppercase tracking-widest mb-3 block">مسارات المشي</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#e8d9bc] mb-4" style={{ fontFamily: 'Cairo, sans-serif' }}>
              حِينَ تَمْشِي مُنْتَبِهًا — الطَّبِيعَةُ تَحْكِي
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4 mb-12">
            <img src="/images/IMG_8004.jpg" alt="تأمل في البرية" className="rounded-2xl w-full h-64 object-cover shadow-xl" style={{ filter: 'sepia(0.22) saturate(0.88) brightness(0.88) contrast(1.06)' }} />
            <img src="/images/IMG_7783.jpg" alt="مشي جماعي" className="rounded-2xl w-full h-64 object-cover shadow-xl" style={{ filter: 'sepia(0.22) saturate(0.88) brightness(0.88) contrast(1.06)' }} />
            <img src="/images/IMG_7991.jpg" alt="نباتات برية" className="rounded-2xl w-full h-64 object-cover shadow-xl" style={{ filter: 'sepia(0.22) saturate(0.88) brightness(0.88) contrast(1.06)' }} />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { name: 'دير مارسابا', dist: '8 كم', time: '٣–٤ ساعات', level: 'متوسط', desc: 'ثمانية كيلومترات جنوباً — محطة لا تُنسى' },
              { name: 'مطلة البحر الميت', dist: '16 كم', time: 'يوم كامل', level: 'متقدم', desc: '430 متراً تحت مستوى البحر — منظر خلّاب' },
              { name: 'خربة المردّ', dist: '10 كم', time: '٤–٥ ساعات', level: 'متوسط', desc: 'آثار بيزنطية وطرق حج قديمة' },
              { name: 'مسار الليل', dist: '2 كم', time: '١–٢ ساعة', level: 'سهل', desc: 'اكتشاف البرية تحت نور القمر' },
              { name: 'درب البرية الكبير', dist: '12 كم', time: '٥–٦ ساعات', level: 'متقدم', desc: 'تضاريس صخرية نادرة وبرية شاسعة' },
            ].map((trail, i) => (
              <div key={i} className="bg-[#1a1108] border border-[#3a2810] rounded-xl p-5 hover:border-[#b87333]/50 transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[#e8d9bc] font-bold">{trail.name}</h3>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${trail.level === 'سهل' ? 'bg-green-900/40 text-green-400' : trail.level === 'متوسط' ? 'bg-yellow-900/40 text-yellow-400' : 'bg-red-900/40 text-red-400'}`}>{trail.level}</span>
                </div>
                <p className="text-[#7a6050] text-sm mb-3">{trail.desc}</p>
                <div className="flex gap-4 text-xs text-[#9a7850]">
                  <span>📏 {trail.dist}</span>
                  <span>⏱ {trail.time}</span>
                </div>
              </div>
            ))}
            <div className="bg-[#1a1108] border border-[#3a2810] rounded-xl p-5 flex items-center justify-center text-center">
              <div>
                <Mountain className="text-[#b87333] mx-auto mb-3" size={32} />
                <p className="text-[#a89070] text-sm">ارتدِ أحذية مشي وملابس مريحة</p>
                <p className="text-[#7a6050] text-xs mt-1">مرشدنا معك في كل خطوة</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======= CAMPING ======= */}
      <section id="camping" className="relative py-24 px-4 overflow-hidden">
        <img src="/images/IMG_7777.jpg" alt="التخييم" className="absolute inset-0 w-full h-full object-cover" style={{ filter: 'sepia(0.25) saturate(0.8) brightness(0.3) contrast(1.1)' }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0d0a06] via-transparent to-[#0d0a06]" />
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-[#b87333] text-sm font-semibold uppercase tracking-widest mb-3 block">التخييم والسهرة</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#e8d9bc] mb-4" style={{ fontFamily: 'Cairo, sans-serif' }}>
              حِينَ يَسْكُتُ كُلُّ شَيْءٍ — تَسْمَعُ نَفْسَكَ
            </h2>
            <p className="text-[#c9a96e] text-lg italic">«الصَّمْتُ فِي الْبَرِّيَّةِ لَيْسَ فَرَاغًا — هُوَ امْتِلَاءٌ مِنْ نَوْعٍ آخَرَ»</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { img: '/images/IMG_7777.jpg', title: 'خيمة تل القمر', desc: 'واسعة للمجموعات — فيها فرشات — يمكنك إحضار ملاءتك' },
              { img: '/images/IMG_8024.jpg', title: 'بيت الضيافة', desc: 'غرف بأسرة للخصوصية — يحجز مسبقاً' },
              { img: '/images/IMG_7782.jpg', title: 'خيمتك أنت', desc: 'فضاء مفتوح وحرية تامة في البرية الشاسعة' },
            ].map((opt, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden h-64 group cursor-pointer">
                <img src={opt.img} alt={opt.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" style={{ filter: 'sepia(0.22) saturate(0.85) brightness(0.65) contrast(1.08)' }} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 right-0 left-0 p-5">
                  <h3 className="text-[#e8d9bc] font-bold text-lg">{opt.title}</h3>
                  <p className="text-[#a89070] text-sm mt-1">{opt.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 grid grid-cols-3 gap-4 max-w-xl mx-auto text-center">
            {['🔭 رصد النجوم', '🍖 الزرب على الجمر', '🎵 أمسية موسيقية حية'].map((item, i) => (
              <div key={i} className="bg-black/40 backdrop-blur-sm border border-[#3a2810] rounded-xl p-3 text-[#c9a96e] text-sm">{item}</div>
            ))}
          </div>
        </div>
      </section>

      {/* ======= FOOD ======= */}
      <section id="food" className="py-24 px-4 bg-[#080604]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-[#b87333] text-sm font-semibold uppercase tracking-widest mb-3 block">الزرب والمذاق</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#e8d9bc] mb-4" style={{ fontFamily: 'Cairo, sans-serif' }}>
              أَكْلٌ لَا تَجِدُهُ فِي أَيِّ مَطْعَمٍ
            </h2>
            <p className="text-[#a89070] text-lg">الْبَسَاطَةُ الْحَقِيقِيَّةُ هِيَ الرَّفَاهِيَّةُ الْحَقِيقِيَّةُ</p>
          </div>
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="grid grid-cols-2 gap-4">
              <img src="/images/IMG_7769.jpg" alt="الزرب" className="rounded-2xl w-full h-60 object-cover shadow-xl col-span-1" style={{ filter: 'sepia(0.22) saturate(0.88) brightness(0.9) contrast(1.06)' }} />
              <img src="/images/IMG_7752.jpg" alt="مشاوي" className="rounded-2xl w-full h-60 object-cover shadow-xl col-span-1" style={{ filter: 'sepia(0.22) saturate(0.88) brightness(0.9) contrast(1.06)' }} />
              <img src="/images/IMG_7761.jpg" alt="طعام" className="rounded-2xl w-full h-48 object-cover shadow-xl" style={{ filter: 'sepia(0.22) saturate(0.88) brightness(0.9) contrast(1.06)' }} />
              <img src="/images/IMG_7756.jpg" alt="قهوة" className="rounded-2xl w-full h-48 object-cover shadow-xl" style={{ filter: 'sepia(0.22) saturate(0.88) brightness(0.9) contrast(1.06)' }} />
            </div>
            <div>
              <div className="bg-[#1a1108] border border-[#3a2810] rounded-2xl p-6 mb-6">
                <h3 className="text-[#b87333] font-bold text-xl mb-3">🪨 الزَّرَبُ</h3>
                <p className="text-[#a89070] leading-relaxed">لحم يطهى في باطن الأرض على جمر حقيقي لساعات. لا غاز ولا فرن — فقط الأرض والنار والوقت. يُطلب مسبقاً عند الحجز.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {['🥩 مشاوي على الجمر', '🍚 منسف الجماعة', '🍳 قلاية بندورة', '🍮 كنافة على الفحم', '☕ قهوة عربية', '🍳 صاجية حارة'].map((item, i) => (
                  <div key={i} className="bg-[#13100a] border border-[#2a1f12] rounded-xl p-3 text-[#c9a96e] text-sm text-center">{item}</div>
                ))}
              </div>
              <p className="text-[#7a6050] text-sm mt-4 italic text-center">«يُمْكِنُكَ أَيْضًا أَنْ تَطْبُخَ بِنَفْسِكَ فِي الْخَلَاءِ»</p>
            </div>
          </div>
        </div>
      </section>

      {/* ======= WORKSHOPS ======= */}
      <section id="workshops" className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#b87333] text-sm font-semibold uppercase tracking-widest mb-3 block">ورشات العمل</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#e8d9bc] mb-5" style={{ fontFamily: 'Cairo, sans-serif' }}>
                اكْسِرْ جُدْرَانَ الْمَكْتَبِ وَالرُّوتِينِ
              </h2>
              <p className="text-[#a89070] leading-relaxed mb-8">
                «الْإِنْسَانُ يَتَغَيَّرُ حِينَ تَتَغَيَّرُ بِيئَتُهُ. أَخْرِجْهُ مِنَ الْمَكْتَبِ إِلَى الطَّبِيعَةِ الْمَفْتُوحَةِ — وَسَتَتَفَاجَأُ كَمْ يُصْبِحُ مُخْتَلِفًا»
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { emoji: '🎨', title: 'فنية إبداعية', desc: 'في الهواء الطلق' },
                  { emoji: '🧘', title: 'تأمل وحضور', desc: 'في قلب البرية' },
                  { emoji: '💆', title: 'تفريغ نفسي', desc: 'مساحة آمنة' },
                  { emoji: '🤝', title: 'بناء الفريق', desc: 'تجارب جماعية حقيقية' },
                ].map((item, i) => (
                  <div key={i} className="bg-[#1a1108] border border-[#3a2810] rounded-xl p-4 hover:border-[#b87333]/50 transition-colors">
                    <span className="text-2xl block mb-2">{item.emoji}</span>
                    <p className="text-[#e8d9bc] font-semibold text-sm">{item.title}</p>
                    <p className="text-[#7a6050] text-xs mt-1">{item.desc}</p>
                  </div>
                ))}
              </div>
              <div className="bg-[#1a1108] border border-[#b87333]/30 rounded-xl p-4">
                <p className="text-[#b87333] font-semibold text-sm mb-2">✨ لمن هي؟</p>
                <p className="text-[#a89070] text-sm">الشركات والمؤسسات · مجموعات النساء · الأطفال · الشباب · المخيمات الكشفية</p>
              </div>
            </div>
            <div className="relative">
              <img src="/images/IMG_8009.jpg" alt="ورشة تأمل" className="rounded-2xl w-full h-[480px] object-cover shadow-2xl" style={{ filter: 'sepia(0.22) saturate(0.85) brightness(0.88) contrast(1.06)' }} />
              <div className="absolute -bottom-4 -right-4 bg-[#b87333] text-white rounded-xl p-4 shadow-xl">
                <p className="font-bold text-sm">كل ورشة مُصمَّمة</p>
                <p className="text-xs opacity-80">خصوصاً لمجموعتك</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======= OFF-ROAD ======= */}
      <section id="offroad" className="relative py-24 px-4 overflow-hidden">
        <img src="/images/IMG_7776.jpg" alt="البحر الميت" className="absolute inset-0 w-full h-full object-cover" style={{ filter: 'sepia(0.22) saturate(0.8) brightness(0.28) contrast(1.1)' }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0d0a06] via-black/20 to-[#0d0a06]" />
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-[#b87333] text-sm font-semibold uppercase tracking-widest mb-3 block">الدفع الرباعي</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#e8d9bc] mb-5" style={{ fontFamily: 'Cairo, sans-serif' }}>
              الْبَرِّيَّةُ لَا تُرَى مِنَ الطَّرِيقِ الْمُعَبَّدِ
            </h2>
            <p className="text-[#c9a96e] text-lg max-w-3xl mx-auto leading-relaxed">
              مِنْ أَعَالِي بَرِّيَّةِ الْقُدْسِ، تَنْبَسِطُ أَمَامَكَ إِطْلَالَةٌ بَانُورَامِيَّةٌ سَاحِرَةٌ عَلَى الْبَحْرِ الْمَيِّتِ وَجِبَالِ الْأُرْدُنِّ، حَيْثُ تَقِفُ عِنْدَ أَخْفَضِ نُقْطَةٍ عَلَى سَطْحِ الْأَرْضِ، عَلَى عُمْقٍ يُقَارِبُ <strong className="text-[#b87333]">430 مِتْرًا</strong> تَحْتَ مُسْتَوَى سَطْحِ الْبَحْرِ.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <img src="/images/IMG_7776.jpg" alt="بانوراما البحر الميت" className="rounded-2xl w-full h-64 object-cover shadow-xl md:col-span-2" style={{ filter: 'sepia(0.2) saturate(0.85) brightness(0.85) contrast(1.08)' }} />
            <div className="flex flex-col gap-4">
              <img src="/images/IMG_7747.jpg" alt="قافلة الجيبات" className="rounded-2xl w-full h-[120px] object-cover shadow-xl" style={{ filter: 'sepia(0.22) saturate(0.85) brightness(0.85) contrast(1.06)' }} />
              <img src="/images/IMG_7734.jpg" alt="مطلة البحر الميت" className="rounded-2xl w-full h-[120px] object-cover shadow-xl" style={{ filter: 'sepia(0.22) saturate(0.85) brightness(0.85) contrast(1.06)' }} />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            {['🚙 قافلة جيبات وعرة', '🏔️ تضاريس صخرية نادرة', '👀 مطلات متعددة', '🏢 للشركات: 4×4 يبني الفريق'].map((item, i) => (
              <div key={i} className="bg-black/40 backdrop-blur-sm border border-[#3a2810] rounded-xl p-4 text-[#c9a96e] text-sm text-center">{item}</div>
            ))}
          </div>
        </div>
      </section>

      {/* ======= CONTACT ======= */}
      <section id="contact" className="py-24 px-4 bg-[#080604]">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-[#b87333] text-sm font-semibold uppercase tracking-widest mb-3 block">تواصل معنا</span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#e8d9bc] mb-4" style={{ fontFamily: 'Cairo, sans-serif' }}>
            احْجُزْ تَجْرِبَتَكَ
          </h2>
          <p className="text-[#a89070] text-lg mb-12">نُؤْمِنُ أَنَّ التَّجْرِبَةَ الْحَقِيقِيَّةَ تُغَيِّرُ النَّاسَ</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {[
              { icon: <MessageCircle size={24} />, label: 'واتساب', value: '970598903500+', href: 'https://wa.me/970598903500', color: 'bg-green-900/30 border-green-800/50 hover:border-green-600' },
              { icon: <Phone size={24} />, label: 'هاتف', value: '0542054914', href: 'tel:0542054914', color: 'bg-[#1a1108] border-[#3a2810] hover:border-[#b87333]/50' },
              { icon: <Mail size={24} />, label: 'بريد إلكتروني', value: 'talqamar.camp@gmail.com', href: 'mailto:talqamar.camp@gmail.com', color: 'bg-[#1a1108] border-[#3a2810] hover:border-[#b87333]/50' },
              { icon: <Instagram size={24} />, label: 'إنستغرام', value: '@taL_aL_qamar', href: 'https://www.instagram.com/taL_aL_qamar', color: 'bg-purple-900/30 border-purple-800/50 hover:border-purple-500' },
            ].map((item, i) => (
              <a key={i} href={item.href} target="_blank" rel="noopener noreferrer"
                className={`${item.color} border rounded-2xl p-5 flex flex-col items-center gap-3 transition-all duration-200 hover:scale-105`}>
                <div className="text-[#b87333]">{item.icon}</div>
                <p className="text-[#a89070] text-xs font-medium">{item.label}</p>
                <p className="text-[#e8d9bc] text-sm font-semibold break-all text-center">{item.value}</p>
              </a>
            ))}
          </div>
          <div className="bg-[#1a1108] border border-[#3a2810] rounded-2xl p-6 flex items-center justify-center gap-3">
            <MapPin className="text-[#b87333] flex-shrink-0" size={22} />
            <p className="text-[#a89070] text-sm">دار صلاح، شرق بيت لحم، برية القدس، فلسطين</p>
          </div>
          <div className="mt-8">
            <a href="https://wa.me/970598903500?text=أهلاً،%20أود%20الاستفسار%20عن%20حجز%20تجربة%20في%20تل%20القمر"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#b87333] hover:bg-[#d4904a] text-white font-bold text-xl px-10 py-5 rounded-2xl shadow-2xl transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(184,115,51,0.4)]">
              <MessageCircle size={26} /> تواصل عبر واتساب الآن
            </a>
          </div>
        </div>
      </section>

      {/* ======= FOOTER ======= */}
      <footer className="bg-[#0a0705] border-t border-[#2a1f12] py-10 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="تل القمر" className="h-14 w-auto drop-shadow-lg" />
            <div>
              <p className="text-[#e8d9bc] font-bold text-lg" style={{ fontFamily: 'Cairo, sans-serif' }}>تَلُّ الْقَمَرِ</p>
              <p className="text-[#7a6050] text-sm">حَيْثُ تَلْتَقِي الْبَرِّيَّةُ بِالرُّوحِ</p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            {navItems.slice(0, 5).map(item => (
              <button key={item.id} onClick={() => scrollToSection(item.id)} className="text-[#7a6050] hover:text-[#c9a96e] text-sm transition-colors">
                {item.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <a href="https://www.instagram.com/taL_aL_qamar" target="_blank" rel="noopener noreferrer" className="text-[#7a6050] hover:text-[#c9a96e] transition-colors"><Instagram size={20} /></a>
            <a href="https://wa.me/970598903500" target="_blank" rel="noopener noreferrer" className="text-[#7a6050] hover:text-[#c9a96e] transition-colors"><MessageCircle size={20} /></a>
            <a href="tel:0542054914" className="text-[#7a6050] hover:text-[#c9a96e] transition-colors"><Phone size={20} /></a>
          </div>
        </div>
        <div className="text-center mt-8 text-[#4a3820] text-sm">
          <Star className="inline-block ml-2" size={12} />
          جميع الحقوق محفوظة لمركز تل القمر © 2026
          <Star className="inline-block mr-2" size={12} />
        </div>
      </footer>
    </div>
  )
}
